import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from '@/i18n';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import { shiftService } from '@/api/domains/shift';
import type { CompanyShiftCatalogItem } from '@/api/domains/shift';
import { workingDayService } from '@/api/domains/workingDay';
import type { WorkingDayItem } from '@/api/domains/workingDay';
import { workingDayTypeCatalogService } from '@/api/domains/catalog/workingDayType';
import { useUI } from '@/hooks/ui/useUI';
import { useExport } from '@/hooks/ui/useExport';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { ROUTE_DEFINITIONS, SCREEN_PREFERENCE_CODES } from '@/routes';
import { ShiftAssignedIndicator } from '@/components/features/shift/ShiftAssignedIndicator';
import { ShiftResolutionStatusIndicator } from '@/components/features/shift/ShiftResolutionStatusIndicator';


const WORKING_DAY_CATALOG_PAGE_SIZE = 1000;


type ShiftCatalogFilters = {
  workingDayType: string;
  workingDayId: string;
  status: string;
};

export interface UseShiftCatalogListResult {
  rows: CompanyShiftCatalogItem[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<CompanyShiftCatalogItem>[];
  actions: TableAction<CompanyShiftCatalogItem>[];
  onSearch: (term: string) => void;
  onEnterSearch: () => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  isLoading: boolean;
  isExporting: boolean;
  onExport: () => Promise<void>;
  filters: ShiftCatalogFilters;
  onFilterChange: (key: string, value: string | string[] | undefined) => void;
  workingDayTypeOptions: { value: string; label: string }[];
  workingDayOptions: { value: string; label: string }[];
}

export function useShiftCatalogList(): UseShiftCatalogListResult {
  const { t: translate } = useTranslation();
  const { showApiError, showSuccess, openModal } = useUI();
  const { canDelete } = usePagePermissions();
  const navigate = useNavigate();

  const [allJornadas, setAllJornadas] = useState<WorkingDayItem[]>([]);
  const [workingDayTypeOptions, setWorkingDayTypeOptions] =
    useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [tiposResult, jornadasResult] = await Promise.all([
          workingDayTypeCatalogService.getWorkingDayTypes(),
          workingDayService.list({ pageSize: WORKING_DAY_CATALOG_PAGE_SIZE }),
        ]);
        if (cancelled) return;
        if (tiposResult.success && tiposResult.data) {
          setWorkingDayTypeOptions(
            tiposResult.data.map((t) => ({ value: t.description, label: t.description })),
          );
        } else if (!tiposResult.success) {
          showApiError(tiposResult, translate('schedule:shift.messages.loadFiltersError'));
        }
        if (jornadasResult.success && jornadasResult.data) {
          setAllJornadas(jornadasResult.data.items);
        } else if (!jornadasResult.success) {
          showApiError(jornadasResult, translate('schedule:shift.messages.loadFiltersError'));
        }
      } catch {
        if (!cancelled) {
          showApiError(
            { success: false, error: undefined },
            translate('schedule:shift.messages.loadFiltersError'),
          );
        }
      }
    })();
    return () => { cancelled = true; };
  }, [showApiError, translate]);

  const todosOption = useMemo(
    () => ({ value: '', label: translate('schedule:shift.filters.all') }),
    [translate],
  );

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<ShiftCatalogFilters>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<CompanyShiftCatalogItem> | null> => {
      const response = await shiftService.getCompanyShifts(
        {
          page: params.page,
          pageSize: params.pageSize,
          searchText: params.searchText,
          orderBy: params.sort?.key,
          orderDirection: params.sort?.key ? params.sort.direction.toUpperCase() : undefined,
          workingDayType: params.filters.workingDayType || undefined,
          workingDayId: params.filters.workingDayId
            ? Number(params.filters.workingDayId)
            : undefined,
          status: params.filters.status || undefined,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code !== 'ABORTED') {
          showApiError(response, translate('schedule:shift.messages.loadError'));
        }
        return null;
      }

      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading: isLoading,
    onSearch,
    onSortChange,
    flushSearch,
    filters,
    setFilters,
    refetch,
  } = usePaginatedList<CompanyShiftCatalogItem, ShiftCatalogFilters>({
    fetchFn,
    initialFilters: { workingDayType: '', workingDayId: '', status: '' },
    orderByMap: {
      id: 'id',
      shiftCode: 'shiftCode',
      shiftName: 'shiftName',
      workingDayType: 'workingDayType',
      workingDay: 'workingDay',
      createdAt: 'createdAt',
      status: 'status',
    },
    screenCode: SCREEN_PREFERENCE_CODES.TURNOS_ROTATIVOS_FIJOS_SHIFTS,
  });

  const onFilterChange = useCallback(
    (key: string, value: string | string[] | undefined) => {
      const newValue = Array.isArray(value) ? (value[0] ?? '') : (value ?? '');
      if (key === 'workingDayType') {
        setFilters((prev) => ({ ...prev, workingDayType: newValue, workingDayId: '' }));
      } else {
        setFilters((prev) => ({ ...prev, [key]: newValue }));
      }
    },
    [setFilters],
  );

  const workingDayOptions = useMemo(() => {
    const selectedType = filters.workingDayType;
    const filtered = selectedType
      ? allJornadas.filter((j) => j.type === selectedType)
      : allJornadas;
    return [todosOption, ...filtered.map((j) => ({ value: String(j.id), label: j.name }))];
  }, [allJornadas, filters.workingDayType, todosOption]);

  // El export solo respeta los filtros avanzados (tipo de jornada, jornada, estado);
  // el texto de búsqueda libre se ignora deliberadamente.
  const { onExport, isExporting } = useExport({
    exportFn: () =>
      shiftService.exportCompanyShifts({
        workingDayType: filters.workingDayType || undefined,
        workingDayId: filters.workingDayId ? Number(filters.workingDayId) : undefined,
        status: filters.status || undefined,
      }),
    successMessage: translate('schedule:shift.messages.exportSuccess'),
    errorMessage: translate('schedule:shift.messages.exportError'),
  });

  const columns: TableColumn<CompanyShiftCatalogItem>[] = useMemo(
    () => [
      {
        key: 'id',
        header: translate('schedule:shift.columns.id'),
        sortable: true,
        width: 80,
      },
      {
        key: 'shiftCode',
        header: translate('schedule:shift.columns.shiftCode'),
        sortable: true,
        render: (row: CompanyShiftCatalogItem) => (
          <span className="inline-flex items-center">
            {row.shiftCode || '-'}
            {row.hasAssignments && <ShiftAssignedIndicator />}
          </span>
        ),
      },
      {
        key: 'shiftName',
        header: translate('schedule:shift.columns.shiftName'),
        sortable: true,
        render: (row: CompanyShiftCatalogItem) => row.shiftName || '-',
      },
      {
        key: 'workingDayType',
        header: translate('schedule:shift.columns.workingDayType'),
        sortable: true,
        render: (row: CompanyShiftCatalogItem) => row.workingDayType || '-',
      },
      {
        key: 'workingDay',
        header: translate('schedule:shift.columns.workingDay'),
        sortable: true,
        render: (row: CompanyShiftCatalogItem) => row.workingDay || '-',
      },
      {
        key: 'createdAt',
        header: translate('schedule:shift.columns.createdAt'),
        sortable: true,
      },
      {
        key: 'resolution',
        header: translate('schedule:shift.columns.resolution'),
        align: 'center',
        // El contenido es un ícono, no texto — 'wrap' evita que Table intente
        // envolverlo en su propio mecanismo de tooltip por truncamiento
        // (pensado para texto), que colisionaba con el tooltip propio del ícono.
        overflowStrategy: 'wrap',
        render: (row: CompanyShiftCatalogItem) => (
          <ShiftResolutionStatusIndicator
            isExceptionalWorkingDay={row.isExceptionalWorkingDay}
            dtResolutionExpired={row.dtResolutionExpired}
            dtResolutionExpiryDate={row.dtResolutionExpiryDate}
          />
        ),
      },
      {
        key: 'status',
        header: translate('schedule:shift.columns.status'),
        sortable: true,
        render: (row: CompanyShiftCatalogItem) => row.status || '-',
      },
    ],
    [translate],
  );

  const actions: TableAction<CompanyShiftCatalogItem>[] = useMemo(
    () => [
      {
        id: 'detail',
        label: translate('common:tableActions.detail'),
        icon: 'eye',
        onClick: (item: CompanyShiftCatalogItem) => {
          void navigate(ROUTE_DEFINITIONS.TURNO_EDITAR.path.replace(':id', String(item.id)));
        },
        // Always visible — the destination page decides read-only vs editable
        // based on `canEdit && !hasAssignments`.
      },
      {
        id: 'delete',
        label: translate('common:tableActions.delete'),
        icon: 'close',
        onClick: (item: CompanyShiftCatalogItem) => {
          openModal('ConfirmModal', {
            variant: 'error',
            icon: 'alert',
            title: translate('schedule:shift.modal.delete.title'),
            bodyText: translate('schedule:shift.modal.delete.body', { name: item.shiftName }),
            confirmText: translate('schedule:shift.modal.delete.confirm'),
            onConfirm: async () => {
              const response = await shiftService.deleteCompanyShift(item.id);
              if (response.success) {
                showSuccess(translate('schedule:shift.messages.deleteSuccess'));
                refetch();
              } else {
                showApiError(response, translate('schedule:shift.messages.deleteError'));
              }
            },
          });
        },
        hidden: (item: CompanyShiftCatalogItem) => !canDelete || item.hasAssignments,
        variant: 'danger',
      },
    ],
    [translate, canDelete, navigate, openModal, showSuccess, showApiError, refetch],
  );

  const workingDayTypeOptionsWithAll = useMemo(
    () => [todosOption, ...workingDayTypeOptions],
    [todosOption, workingDayTypeOptions],
  );

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    onSearch,
    onEnterSearch: flushSearch,
    onSortChange,
    isLoading,
    isExporting,
    onExport,
    filters,
    onFilterChange,
    workingDayTypeOptions: workingDayTypeOptionsWithAll,
    workingDayOptions,
  };
}
